import { useCallback, useEffect, useState } from 'react';
import {
  PATIENT_CATEGORY_LABELS, formatRupiah, MAX_PAGE_SIZE,
  type PatientCategory, type PriceSnapshot, type PriceHistoryRow, type Paginated,
} from 'shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { usePagedList } from '../hooks/usePagedList';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';

interface Named { id: string; code: string; name: string }

const CATEGORIES: PatientCategory[] =
  ['non_jkn', 'jkn_thalassemia', 'jkn_leukemia', 'jkn_hemodialysis'];

const EMPTY = { basePrice: '', discountAmount: '0', subsidyAmount: '0', effectiveFrom: '', note: '' };

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('id-ID',
  { day: '2-digit', month: 'short', year: 'numeric' });

export function Pricing() {
  const { can } = useAuth();

  /** Lookups for the key selector — short lists, fetched whole rather than paged. */
  const [components, setComponents] = useState<Named[]>([]);
  const [branches, setBranches] = useState<Named[]>([]);

  const [componentId, setComponentId] = useState('');
  const [category, setCategory] = useState<PatientCategory>('non_jkn');
  const [branchId, setBranchId] = useState('');

  const [current, setCurrent] = useState<PriceSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // The key is a required filter, not a search term: a history mixing several keys would
  // show two rows both "in force" without either being wrong.
  const history = usePagedList<PriceHistoryRow>('/prices/history', {
    params: { componentTypeId: componentId, patientCategory: category, branchId: branchId || undefined },
    enabled: componentId !== '',
  });

  useEffect(() => {
    (async () => {
      try {
        const [c, b] = await Promise.all([
          api.get<Paginated<Named>>(`/blood/components?pageSize=${MAX_PAGE_SIZE}`),
          api.get<Paginated<Named>>(`/branches?pageSize=${MAX_PAGE_SIZE}`),
        ]);
        setComponents(c.items);
        setBranches(b.items);
        if (c.items[0]) setComponentId(c.items[0].id);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Gagal memuat data.');
      }
    })();
  }, []);

  const loadCurrent = useCallback(async () => {
    if (!componentId) return;
    const qs = new URLSearchParams({ componentTypeId: componentId, patientCategory: category });
    if (branchId) qs.set('branchId', branchId);
    // A missing tariff is a normal state for a combination nobody has priced yet.
    setCurrent(await api.get<PriceSnapshot>(`/prices/resolve?${qs}`).catch(() => null));
  }, [componentId, category, branchId]);

  useEffect(() => { void loadCurrent(); }, [loadCurrent]);

  function openForm() {
    setForm(EMPTY);
    setFormError(null);
    setOpen(true);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      await api.post('/prices', {
        componentTypeId: componentId,
        patientCategory: category,
        branchId: branchId || null,
        basePrice: form.basePrice.replace(/\D/g, ''),
        discountAmount: form.discountAmount.replace(/\D/g, '') || '0',
        subsidyAmount: form.subsidyAmount.replace(/\D/g, '') || '0',
        effectiveFrom: new Date(form.effectiveFrom).toISOString(),
        note: form.note || null,
      });
      setOpen(false);
      setNotice('Tarif baru ditetapkan. Tarif sebelumnya ditutup, nominalnya tidak diubah.');
      history.refresh();
      await loadCurrent();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Gagal menetapkan tarif.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <h2>Tarif BPPD</h2>
        <p>
          Menaikkan tarif berarti menutup tarif lama dan menyisipkan yang baru. Nominal
          tarif yang sudah tercatat tidak pernah diubah, supaya pesanan lama tetap
          memakai harga saat pesanan itu dibuat.
        </p>
      </div>

      {error && <div className="msg error">{error}</div>}
      {notice && <div className="msg ok">{notice}</div>}

      <div className="card stack">
        <div className="grid-2">
          <div className="field">
            <label className="label" htmlFor="p-comp">Komponen</label>
            <select id="p-comp" value={componentId} onChange={(e) => setComponentId(e.target.value)}>
              {components.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="label" htmlFor="p-cat">Kategori pasien</label>
            <select id="p-cat" value={category} onChange={(e) => setCategory(e.target.value as PatientCategory)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{PATIENT_CATEGORY_LABELS[c]}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="label" htmlFor="p-branch">Cabang</label>
            <select id="p-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">Tarif nasional</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.code} · {b.name}</option>)}
            </select>
          </div>
        </div>

        {current ? (
          <div className="row" style={{ gap: '2rem', alignItems: 'baseline' }}>
            <div>
              <span className="label">Dibayar rumah sakit</span>
              <div style={{ fontSize: '1.6rem', fontWeight: 500, color: 'var(--accent-strong)' }}>
                {formatRupiah(current.payableAmount)}
              </div>
            </div>
            <div style={{ fontSize: 13 }} className="muted">
              <div>Dasar {formatRupiah(current.basePrice)}</div>
              {current.discountAmount !== '0' && <div>Diskon −{formatRupiah(current.discountAmount)}</div>}
              {current.subsidyAmount !== '0' && <div>Subsidi −{formatRupiah(current.subsidyAmount)}</div>}
            </div>
            <span className={current.source === 'branch' ? 'pill accent' : 'pill'}>
              {current.source === 'branch' ? 'override cabang' : 'tarif nasional'}
            </span>
          </div>
        ) : (
          <p className="muted">Belum ada tarif untuk kombinasi ini.</p>
        )}
      </div>

      <div>
        <h3 style={{ marginBottom: '0.7rem' }}>Riwayat tarif</h3>
        <DataTable
          list={history}
          searchPlaceholder="Cari catatan tarif…"
          action={can('price:write') ? { label: 'Tetapkan tarif baru', onClick: openForm } : undefined}
          empty="Belum ada riwayat untuk kombinasi ini."
          emptyFiltered="Tidak ada catatan tarif yang cocok dengan pencarian."
          columns={[
            'Berlaku',
            { label: 'Dasar', align: 'right' },
            { label: 'Dibayar', align: 'right' },
            'Status',
            'Catatan',
          ]}
        >
          {history.items.map((h) => (
            <tr key={h.id}>
              <td className="mono" style={{ fontSize: 12.5 }}>
                {fmtDate(h.effectiveFrom)} → {h.effectiveTo ? fmtDate(h.effectiveTo) : 'sekarang'}
              </td>
              <td className="mono" style={{ textAlign: 'right' }}>{formatRupiah(h.basePrice)}</td>
              <td className="mono" style={{ textAlign: 'right', fontWeight: 500 }}>
                {formatRupiah(h.payableAmount)}
              </td>
              <td>
                <span className={h.isCurrent ? 'pill accent' : 'pill'}>
                  {h.isCurrent ? 'berjalan' : 'ditutup'}
                </span>
              </td>
              <td className="muted" style={{ fontSize: 12.5 }}>{h.note ?? '—'}</td>
            </tr>
          ))}
        </DataTable>
      </div>

      <Modal
        open={open}
        title="Tetapkan tarif baru"
        description="Berlaku untuk kombinasi komponen, kategori, dan cabang yang sedang dipilih di atas. Tarif lama ditutup, tidak diubah."
        onClose={() => setOpen(false)}
        onSubmit={submit}
        footer={
          <>
            <button type="button" onClick={() => setOpen(false)} disabled={saving}>Batal</button>
            <button type="submit" className="primary" disabled={saving}>
              {saving ? 'Menyimpan…' : 'Tetapkan tarif'}
            </button>
          </>
        }
      >
        <div className="stack" style={{ gap: '0.9rem' }}>
          {formError && <div className="msg error">{formError}</div>}

          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            {components.find((c) => c.id === componentId)?.name ?? '—'} ·{' '}
            {PATIENT_CATEGORY_LABELS[category]} ·{' '}
            {branchId ? branches.find((b) => b.id === branchId)?.code : 'tarif nasional'}
          </p>

          <div className="grid-2">
            <div className="field">
              <label className="label" htmlFor="f-base">Harga dasar (Rp)</label>
              <input id="f-base" value={form.basePrice} required inputMode="numeric" placeholder="490000"
                onChange={(e) => setForm({ ...form, basePrice: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="f-eff">Mulai berlaku</label>
              <input id="f-eff" type="date" value={form.effectiveFrom} required
                onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="f-disc">Diskon (Rp)</label>
              <input id="f-disc" value={form.discountAmount} inputMode="numeric"
                onChange={(e) => setForm({ ...form, discountAmount: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="f-sub">Subsidi (Rp)</label>
              <input id="f-sub" value={form.subsidyAmount} inputMode="numeric"
                onChange={(e) => setForm({ ...form, subsidyAmount: e.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label className="label" htmlFor="f-note">Catatan</label>
              <input id="f-note" value={form.note} placeholder="mis. Kepmenkes HK.01.07/MENKES/504/2024"
                onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
